import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import PCheckbox from "components/PCheckbox";
import PSelect from "components/PSelect";
import { FormSection } from "generated/graphql";
import { ChangeEvent, FC, useState } from "react";
import { useAppSelector } from "redux/hooks";
import {
  deleteSection,
  selectLensEditorForm,
  selectProfileBuilderFields,
  updateFormSections,
} from "redux/slices/lensEditor";
import useAppDispatchWithSnackbar from "../../../helpers/hooks/useAppDispatchWithSnackbar";
import { Delete } from "@mui/icons-material";

interface EditSectionDialogProps {
  open: boolean;
  close: () => void;
  section: FormSection;
}

const EditSectionDialog: FC<EditSectionDialogProps> = ({ open, close, section }) => {
  const snackpatch = useAppDispatchWithSnackbar();
  const [name, setName] = useState<string>(section.name);
  const [outcomeEnabled, setOutcomeEnabled] = useState<boolean>(section.outcomeEnabled ?? false);
  const [validationEnabled, setValidationEnabled] = useState<boolean>(section.validationEnabled ?? false);
  const [formPage, setFormPageId] = useState<number>(section.formPageId);
  const form = useAppSelector(selectLensEditorForm);

  const fields = useAppSelector(selectProfileBuilderFields);

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleOutcomeEnabledChange = (event: ChangeEvent<HTMLInputElement>) => {
    setOutcomeEnabled(event.target.checked);
  };

  const handleValidationEnabledChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValidationEnabled(event.target.checked);
  };

  const save = () => {
    void snackpatch(
      updateFormSections({
        sections: [{ sectionId: section.id, values: { name, outcomeEnabled, validationEnabled, formPage } }],
      }),
      {
        successMessage: "Section updated",
        errorMessage: "Unable to edit section",
      }
    ).then(() => {
      close();
    });
  };

  const canDelete = fields.find((field) => field.sectionId === section.id) === undefined;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Section</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <TextField label="Name" value={name} onChange={handleNameChange} />
          </FormControl>
          <FormControl
            fullWidth
            sx={{
              mt: 2,
            }}>
            <InputLabel>Page</InputLabel>
            <PSelect
              label="Page"
              value={formPage}
              onChange={(evt) => {
                // if value is number, then it's a page id
                if (typeof evt.target.value === "number") {
                  setFormPageId(evt.target.value);
                }
              }}>
              {form.pages.map((page) => (
                <MenuItem key={page.id} value={page.id}>
                  {page.name}
                </MenuItem>
              ))}
            </PSelect>
          </FormControl>
          <PCheckbox label="Outcome Enabled" checked={outcomeEnabled} onChange={handleOutcomeEnabledChange} />
          <PCheckbox label="Validation Enabled" checked={validationEnabled} onChange={handleValidationEnabledChange} />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pl: 2,
        }}>
        <Tooltip title={!canDelete ? "Section has fields" : ""} placement="right">
          <Box>
            <Button
              color="error"
              disabled={!canDelete}
              startIcon={<Delete />}
              onClick={async () => {
                if (form.id && section.id) {
                  await snackpatch(deleteSection({ formId: form.id, formSectionId: section.id }), {
                    successMessage: "Section deleted",
                    errorMessage: "Unable to delete section",
                  });
                  close();
                }
              }}>
              Delete
            </Button>
          </Box>
        </Tooltip>

        <Box
          sx={{
            display: "flex",
            gap: 1,
          }}>
          <Button onClick={close}>Cancel</Button>
          <Button onClick={save} disabled={!name} variant="contained">
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditSectionDialog;
